import { useState } from 'react'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import listPlugin from '@fullcalendar/list'
import { format } from 'date-fns/format'

import { useCalendarData, CalendarData, ExtendedProps } from './useCalendarData'
import FullCalendar from './FullCalendar'
import {
  lungsSrc,
  heartSrc,
  vasculaireSrc,
  congenitalSrc,
  roomSVG,
  placeSVG,
  personSVG,
  selectContributorSVG,
  outsideRoomSVG,
} from './FullCalendar.utils'
import { useTagsDataOptions, getOption, searchString } from '../../../../utils'
import { useFilterCalendar } from './useFilterCalendar'

interface ListCalendarProps {
  data: CalendarData
  showWithoutSession?: boolean
  showHotel?: boolean
  showHiddenFromGenericProgram?: boolean
  showOnlySessions?: boolean
  hideSelectView?: boolean
  disableClick?: boolean
}

export default function ListCalendar({
  data,
  showWithoutSession = true,
  showHotel = false,
  showHiddenFromGenericProgram = false,
  showOnlySessions = false,
  hideSelectView = false,
  disableClick = false,
}: ListCalendarProps) {
  const navigate = useNavigate()
  const theme = useTheme()
  const { i18n, t } = useTranslation()

  const calendarData = useCalendarData(data, {
    showWithoutSession,
    showHotel,
    showHiddenFromGenericProgram,
  })
  const tagsOptions = useTagsDataOptions()

  const [viewType, setViewType] = useState<'sessions' | 'subEvents'>(
    showOnlySessions ? 'sessions' : 'subEvents'
  )
  const [tagFilter, setTagFilter] = useState<string | null>(null)

  const filteredEvents = useFilterCalendar(calendarData, { viewType, tagFilter })

  return (
    <Stack spacing={2} mb={2}>
      {!hideSelectView && (
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Autocomplete
            options={tagsOptions}
            size="small"
            value={getOption(tagsOptions, tagFilter)}
            onChange={(_, value) => setTagFilter(value?.label ?? null)}
            sx={{ width: '25rem' }}
            renderInput={params => (
              <TextField {...params} variant="outlined" label={t('Programme')} />
            )}
          />
          <Button
            onClick={() => {
              setViewType(prev => (prev === 'sessions' ? 'subEvents' : 'sessions'))
            }}
            fullWidth
          >
            {viewType === 'sessions' ? t('Afficher les interventions') : t('Afficher les séances')}
          </Button>
        </Stack>
      )}
      <FullCalendar
        schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
        plugins={[listPlugin]}
        initialView="fullEvent"
        timeZone="local"
        locale={i18n.resolvedLanguage}
        height="auto"
        validRange={{
          start: data?.event.start,
          end: data?.event.end,
        }}
        views={{
          fullEvent: {
            type: 'list',
            buttonText: 'Liste',
            visibleRange: {
              start: data?.event.start,
              end: data?.event.end,
            },
          },
        }}
        headerToolbar={false}
        // HOTFIX JFEF
        events={filteredEvents}
        // events={[...(events ?? []), ...(sessions ?? [])]}
        eventClick={info => {
          if (disableClick) return
          const extendedProps = (info.event.extendedProps as ExtendedProps) ?? {}
          const subEventId = extendedProps?.subEventId.toString() ?? ''
          const sessionId = extendedProps?.sessionId.toString() ?? ''

          if (extendedProps?.isSession) {
            navigate(`session/${sessionId}${subEventId ? `?subEventIds=${subEventId}` : ''}`)
          } else {
            navigate(`subEvent/${subEventId}${sessionId ? `?sessionId=${sessionId}` : ''}`)
          }
        }}
        eventDidMount={info => {
          const extendedProps = (info.event.extendedProps as ExtendedProps) ?? {}
          if (extendedProps.sessions?.[0]?.color) {
            info.el.style.backgroundColor = `${extendedProps.sessions[0].color}11`
            info.el.style.borderColor = extendedProps.sessions[0].color
          }

          // Hide time and graphic for detail view
          const timeEl = info.el.getElementsByClassName('fc-list-event-time')[0] as HTMLElement
          if (timeEl) {
            if (extendedProps?.isDetail) {
              timeEl.style.opacity = '0'
            }
          }

          // Hide graphic for detail view
          // and replace it with icons based on type
          const graphEl = info.el.getElementsByClassName('fc-list-event-graphic')[0] as HTMLElement
          if (graphEl) {
            const child = graphEl.getElementsByClassName('fc-list-event-dot')[0] as HTMLElement
            const divFlex = document.createElement('div')
            divFlex.style.display = 'flex'
            divFlex.style.flexDirection = 'column'
            divFlex.style.alignItems = 'center'
            graphEl.appendChild(divFlex)
            if (extendedProps?.isDetail) {
              graphEl.style.opacity = '0'
              const emptyNode = document.createElement('div')
              graphEl.replaceChild(emptyNode, child)
            }
            if (extendedProps.type?.some(type => searchString(type, 'thoracique'))) {
              const icon = document.createElement('img')
              icon.src = lungsSrc
              icon.style.height = '24px'
              icon.style.width = '24px'
              divFlex.appendChild(icon)
              graphEl.className = 'fc-list-event-graphic fc-list-event-no-graphic'
            }
            if (extendedProps.type?.some(type => searchString(type, 'congenital'))) {
              const icon = document.createElement('img')
              icon.src = congenitalSrc
              icon.style.height = '24px'
              icon.style.width = '24px'
              divFlex.appendChild(icon)
              graphEl.className = 'fc-list-event-graphic fc-list-event-no-graphic'
            }
            if (extendedProps.type?.some(type => searchString(type, 'vasculaire'))) {
              const icon = document.createElement('img')
              icon.src = vasculaireSrc
              icon.style.height = '24px'
              icon.style.width = '24px'
              divFlex.appendChild(icon)
              graphEl.className = 'fc-list-event-graphic fc-list-event-no-graphic'
            }
            if (extendedProps.type?.some(type => searchString(type, 'cardiaque'))) {
              const icon = document.createElement('img')
              icon.src = heartSrc
              icon.style.height = '24px'
              icon.style.width = '24px'
              divFlex.appendChild(icon)
              graphEl.className = 'fc-list-event-graphic fc-list-event-no-graphic'
            }
          }
        }}
        // Override the default event content to display more information
        // and make it responsive
        eventContent={info => {
          const responsiveStyle = `<style>
        .fc-override_responsive-inline {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        @media (max-width: 500px) {
          .fc-override_responsive-inline {
            display: flex;
            flex-direction: column-reverse;
            align-items: start;
          }
        }
        </style>`
          const extendedProps = (info.event.extendedProps as ExtendedProps) ?? {}
          const salles = `<div style="width:150px;display:inline-flex;gap:5px;align-items:center;flex-wrap: wrap;">${extendedProps.salles?.map(salle => `<a style="display:inline-flex;align-items:center;">${salle.groupId === extendedProps.location ? roomSVG(info.event.borderColor) : outsideRoomSVG(info.event.borderColor)}${salle.name}</a>`).join('')}</div>`

          const sessions = extendedProps?.sessions.reduce((acc, el) => {
            if (el.shortName) {
              acc.push(
                `<div style="display:inline-flex;gap:5px;align-items:center;background-color:${el.color};color:${theme.palette.getContrastText(el.color ?? 'grey')};border-radius:0.5rem;padding:0.2rem 0.5rem;font-weight:bold;"><a style="flex:1 0 auto;">${el.shortName}</a></div>`
              )
            }
            return acc
          }, [] as string[])

          const title = `<div class="fc-override_responsive-inline" style="gap:5px;width:100%;"><div style="flex: 1 1 0%;"><strong>${info.event.title}</strong>${extendedProps?.description ? `: ${extendedProps.description}` : ''}</div><div style="align-items: center;display: flex; gap: 1rem; flex-wrap: wrap; flex: 0 1 content;">${sessions.join('')}${salles}</div></div>`
          const from = extendedProps?.etablissement
            ? `<div style="display:inline-flex;gap:5px;align-items:center;">${placeSVG(info.event.borderColor)}${extendedProps.etablissement}</div>`
            : ''
          const contributors = extendedProps?.contributors
            ? extendedProps.contributors
                .slice(0, 2)
                .map(
                  el =>
                    `<div style="display:inline-flex;gap:5px;align-items:center;">${selectContributorSVG(el.roles, info.event.borderColor)}<a>${el.user.last_name} ${el.user.first_name}</a></div>`
                )
                .join('')
                .concat(
                  extendedProps.contributors.length > 2
                    ? `<div style="display:inline-flex;gap:5px;align-items:center;">${personSVG(info.event.borderColor)}<a style="color:${info.event.borderColor || 'black'};text-decoration:none;flex:1 0 auto;">+${extendedProps.contributors.length - 2}</a></div>`
                    : ''
                )
            : ''

          return {
            html: `${responsiveStyle}<div>${title}${from || contributors ? `<div style="display:inline-flex;gap:5px;align-items:center;margin-top: 5px;width:100%;flex-wrap: wrap;">${from}${contributors}</div>` : ''}</div>`,
          }
        }}
      />
    </Stack>
  )
}
